import type { SelectChangeEvent } from '@mui/material';
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { getRouteApi } from '@tanstack/react-router';
import type { ChangeEvent, MouseEvent } from 'react';
import { useMemo, useState } from 'react';

import { FormButtonLink } from '@/components/management/formElements/FormButtonLink';
import { StationsTable } from '@/components/management/table/StationsTable';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { useUserSuspenseQuery } from '@/hooks/useUserSuspenseQuery';
import {
  backendApiOrganizationsFindAllOptions,
  backendApiStationsFindAllOptions,
} from '@/modules/api/backend/@tanstack/react-query.gen';
import { FacilityFilter } from '@/types/FacilityFilter';

const routeApi = getRouteApi('/_authenticated/management/stations/');

export function Station() {
  const navigate = routeApi.useNavigate();
  const { data: user } = useUserSuspenseQuery();
  const isAdmin = useIsAdmin();
  const { search, organizationId } = routeApi.useSearch();

  const [filter, setFilter] = useState<FacilityFilter>(FacilityFilter.ALL);

  const userOrgId = user.organization?.organizationId;
  const effectiveOrgId = isAdmin ? organizationId : (userOrgId ?? undefined);

  const { data: organizations } = useQuery(backendApiOrganizationsFindAllOptions());
  const orgList = useMemo(() => organizations ?? [], [organizations]);

  const { data: stations } = useQuery({
    ...backendApiStationsFindAllOptions({
      query: {
        search: search ?? undefined,
        organizationIds: effectiveOrgId ? [effectiveOrgId] : undefined,
      },
    }),
    placeholderData: (prev) => prev,
    enabled: isAdmin ? true : Boolean(userOrgId),
  });

  const handleFilterChange = (_: MouseEvent<HTMLElement>, newFilter: FacilityFilter | null) => {
    if (newFilter !== null) {
      setFilter(newFilter);
    }
  };

  const handleOrgChange = (event: SelectChangeEvent) => {
    if (!isAdmin) return;
    const { value } = event.target;
    navigate({
      replace: true,
      search: (prev) => ({ ...prev, organizationId: value }),
    });
  };

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    navigate({
      replace: true,
      search: (prev) => ({ ...prev, search: value }),
    });
  };

  const getOrganizationMenuItems = () => {
    if (isAdmin) {
      return [
        <MenuItem key="__all" value="">
          <em>Alle</em>
        </MenuItem>,
        ...orgList.map((org) => (
          <MenuItem key={org.id} value={org.id}>
            {org.name}
          </MenuItem>
        )),
      ];
    }

    if (userOrgId) {
      return [
        <MenuItem key={userOrgId} value={userOrgId}>
          {orgList.find((o) => o.id === userOrgId)?.name ?? userOrgId}
        </MenuItem>,
      ];
    }

    return [];
  };

  const getSelectedOrgName = (selected: string) => {
    if (selected === '') return <em>Alle</em>;

    return orgList.find((o) => o.id === selected)?.name ?? selected;
  };

  return (
    <Stack>
      <Stack
        alignItems="flex-end"
        direction="row"
        justifyContent="space-between"
        pb={{ xs: 5, lg: 10 }}
        pt={{ xs: 4, lg: 8 }}
      >
        <Stack direction={{ xs: 'column', lg: 'row' }} gap={2} width="100%">
          <FormControl sx={{ width: 256 }}>
            <InputLabel id="organization-select-label">Organisation</InputLabel>
            <Select
              disabled={!isAdmin}
              id="organization-select"
              label="Organisation"
              labelId="organization-select-label"
              onChange={handleOrgChange}
              renderValue={getSelectedOrgName}
              value={(isAdmin ? organizationId : userOrgId) ?? ''}
            >
              {getOrganizationMenuItems()}
            </Select>
          </FormControl>

          <TextField label="ID oder Name" onChange={handleSearchChange} sx={{ width: 256 }} value={search ?? ''} />
        </Stack>

        <ToggleButtonGroup
          exclusive
          aria-label="Stationen und Parkanlagen filtern"
          color="standard"
          onChange={handleFilterChange}
          value={filter}
        >
          <ToggleButton value={FacilityFilter.ALL}>Alle</ToggleButton>
          <ToggleButton value={FacilityFilter.STATIONS}>Stationen</ToggleButton>
          <ToggleButton value={FacilityFilter.PARKING_FACILITIES}>Anlagen</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {stations ? <StationsTable facilities={stations} filter={filter} userOrgId={userOrgId} /> : null}

      {isAdmin ? (
        <Stack alignItems="flex-end" mt={{ sm: 4, lg: 11.5 }}>
          <FormButtonLink to="/management/stations/create">Station hinzufügen</FormButtonLink>
        </Stack>
      ) : null}
    </Stack>
  );
}
